import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { ShippingDocumentService } from 'src/app/core/services/shipping-document.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { WarehousingSearchService } from '../../transport-operation/services/warehousing-search.service';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AddWarehousingAbnormalRequest } from 'src/app/core/models/transport-operation/add-warehousing-abnormal-request';
import { AddWarehousingAbnormalDetailRequest, FileEvent } from 'src/app/core/models/transport-operation/add-warehousing-abnormal-detail-request';
import { WarehousingAbnormalType } from 'src/app/core/enums/warehousing-abnormal-type.enum';
import { FileUploadDialogComponent } from 'src/app/shared/components/file-upload-dialog/file-upload-dialog.component';
import { UploadMutlipleFileRequest } from '../../transport-operation/components/file-management/interface/upload-mutliple-file-request';
import { MenuService } from 'src/app/core/services/menu.service';

@Component({
  selector: 'laas-remaining-sku',
  templateUrl: './remaining-sku.component.html',
  styleUrls: ['./remaining-sku.component.scss'],
})
export class RemainingSkuComponent extends SessionBaseComponent implements OnInit {

  @Input() whNo: any;
  @Output() visibleChange: EventEmitter<any>;
  @Output() newWhNo: EventEmitter<any>;
  @ViewChild(Dialog) dialog!: Dialog;

  @ViewChild('fileDialog') fileDialog!: FileUploadDialogComponent;

  isRwd: boolean = false;
  currentItemIndex: number = -1;
  isVisible: boolean;
  submitted: boolean;
  vendorSuggests: string[] = [];
  vendorOptions : any[] = [];
  warehousingHeader: any = {};
  remainingSkuForm: FormGroup;
  isShowAbnormalItemFileDialog = true;
  isAbnormalItemFileDialogLoading = false;

  readonly DOC_TYPE_ENUM = 'WarehousingAbnormal';
  readonly DOC_REFERENCE_ENUM = 'Warehousing';
  readonly REMARK_ENUM = 'Warehousing';

  constructor(
    injector: Injector,
    private warehousingSearchService: WarehousingSearchService,
    private shippingDocumentService: ShippingDocumentService,
    private menuDataService: MenuService,
    private fb: FormBuilder) {

    super(injector);
    this.isVisible = false;

    this.submitted = false;
    this.visibleChange = new EventEmitter<any>();
    this.newWhNo = new EventEmitter<any>();
    this.remainingSkuForm = this.fb.group({
      remainingSkuItemForms: this.fb.array([]),
    });
  }

  get remainingSkuItemForms() {
    return this.remainingSkuForm.get('remainingSkuItemForms') as FormArray;
  }

  @Input() get visible(): any {
    return this.isVisible;
  }

  set visible(value: any) {
    this.isVisible = value;
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.initItemForm();
    this.menuDataService.isRwd.subscribe(result => {
      this.isRwd = result;
    });
  }

  resetMainForm() {
    this.remainingSkuForm = this.fb.group({
      remainingSkuItemForms: this.fb.array([]),
    });
    this.initItemForm();
  }

  initItemForm() {
    const item = this.fb.group({
      qty: [],
      sku: [],
      uploadedFiles: [[]]  // 新增一個欄位來存儲上傳的文件
    });
    this.remainingSkuItemForms.push(item);
  }

  addItem() {
    if (this.isAllowedToAddItem()) {
      const item = this.fb.group({
        qty: [],
        sku: [],
        uploadedFiles: [[]]
      });
      this.remainingSkuItemForms.push(item);
    } else {
      this.toastService.error(this.translateService.instant("Sku Item不能超過10個"));
    }
  }

  isAllowedToAddItem(): boolean {
    return this.remainingSkuItemForms.length <= 10;
  }

  deleteItem(i: any) {
    this.remainingSkuItemForms.removeAt(i);
  }

  dialogVisibleChange(event: any): void {
    this.resetMainForm();
    this.visibleChange.emit(event);
  }

  confirmBtnClick(event: any): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('RemainingSku.Confirm.AddedConfirmationMessage'),
      header: this.translateService.instant('RemainingSku.Confirm.AddedConfirmationHeader'),
      accept: () => {
        this.addWarehousingAbnormalityOfRemainingSku();
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  addWarehousingAbnormalityOfRemainingSku(): void {
    if (!this.isAllowedToBuildRequest()) {
      return;
    }
    const detailRequests = this.buildAddWarehousingAbnormalDetailRequest();
    console.log(`addWarehousingAbnormalityOfRemainingSku.detailRequests=${JSON.stringify(detailRequests)}`);
    const request: AddWarehousingAbnormalRequest = {
      whNo: this.whNo,
      userId: this.userProfile.userName,
      abnormalType: WarehousingAbnormalType.RemainingSku,
      abnormalDetailRequests: detailRequests
    };
    this.loader.show();
    this.warehousingSearchService.addWarehousingAbnormalRecord(request)
      .subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('ReceiptFormHeaderDetails.Message.addWarehousingAbnormalSuccess'));
          this.dialogVisibleChange(false);
        },
        error: (err: HttpErrorResponse) => {
          this.toastErrorMessage(err.error);
          this.loader.hide();
        },
        complete: () => {
          this.loader.hide();
        }
      });
  }

  base64Size(base64: string): number {
    let padding = 0;
    if (base64.endsWith('==')) {
      padding = 2;
    } else if (base64.endsWith('=')) {
      padding = 1;
    }
    const base64Length = base64.length - padding;
    return (base64Length * 3) / 4;
  }

  isAllowedToBuildRequest(): boolean {
    const allUploadedFiles: FileEvent['newFiles'] = this.remainingSkuItemForms.value.map((item: any) => item.uploadedFiles).flat();
    const isExceedMaxSize56Mb = allUploadedFiles.reduce((acc, file) => {
      return acc + this.base64Size(file.fileBase64s);
    }, 0) > (56 * 1024 * 1024);

    if (isExceedMaxSize56Mb) {
      this.toastErrorMessage(this.translateService.instant('ReceivingInstruction.Dialog.File.FileSizeLimit'));
    }
    const isAllowed = this.remainingSkuItemForms.value.every((item: any) => {
      return !isNullOrEmpty(item.sku) && !isNullOrEmpty(item.qty);
    });
    if (!isAllowed) {
      this.toastErrorMessage(this.translateService.instant("請確認欄位值不為空"));
    }
    return isAllowed && !isExceedMaxSize56Mb;
  }

  buildAddWarehousingAbnormalDetailRequest(): any {
    const requests: AddWarehousingAbnormalDetailRequest[] = [];
    this.remainingSkuItemForms.controls.forEach(item => {
      let request: AddWarehousingAbnormalDetailRequest = {
        sku: item.value.sku,
        qty: item.value.qty,
        uploadFiles: item.value.uploadedFiles
      }
      requests.push(request);
    });
    return requests;
  }

  /* 顯示fileDialog，用來載入當前的檔案或上傳檔案 */
  showFileUploadDialog(index: number): void {
    this.currentItemIndex = index;

    this.restoreFileData();
    this.fileDialog.changeUploadMode('batch');
    this.fileDialog.visibleChange(true);
  }

  /* 傳入給fileDialog的callback，用來處理fileDialog的儲存，會先把File存到currentItem的uploadedFiles */
  saveFile(event: FileEvent): void {
    if (this.currentItemIndex === -1) return;

    const currentItem = this.remainingSkuItemForms.at(this.currentItemIndex);
    currentItem.patchValue({ uploadedFiles: event.newFiles });
    this.fileDialog.onCloseDialog();
  }

  /* 傳入給fileDialog的callback，用來載入fileDialog的檔案 */
  restoreFileData(): void {
    if (this.currentItemIndex === -1) return;
    const currentItem = this.remainingSkuItemForms.at(this.currentItemIndex);
    const uploadedFiles: FileEvent['newFiles'] = currentItem.get('uploadedFiles')?.value || [];
    /* 從fileDialog拿回來的資料沒有將fileBase64s轉成fileDataUrl，需要手動轉換 */
    this.fileDialog.fileList = uploadedFiles.map((file) => ({
      ...file,
      fileDataUrl: `data:${file.fileType};base64,${file.fileBase64s}`
    }));
  }
}
